/* ** documents type from draft query ** */
import {
  CompanyDocumentType,
  CompanyDraftStatus,
  CreditTransferStatus,
  GetBankIdentitiesByCompanyQuery,
  GetSpvDraftQuery,
  IdentityVerificationStatus,
  SpvDraftDocumentInfosFragment,
  UserInfosFragment,
} from '@blockpulse3/graphql/hooks';
import { IBadge } from '@blockpulse3/ui/ui-hooks';

/**
 * checkSigningComplete.
 * Check if all documents from draft are signed.
 *
 * @param {IDraftDocuments} documents
 * @returns {boolean}
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function checkSigningComplete(documents: any): boolean {
  if (documents) {
    return Object.values(documents || []).every((doc) => {
      /*
				if (doc !== 'SPVDraftDocuments') {
					return doc?.document?.status === DocumentStatus.Signed;
				}
		  */
      return true;
    });
  }
  return false;
}

/**
 * getDraftCurrentStep.
 * Return current step number in spv registry details.
 *
 * @param {string} status
 * @returns {number}
 */
export function getDraftCurrentStep(status: string): number {
  switch (status) {
    case CompanyDraftStatus.SIGNED:
      return 2;
    case CompanyDraftStatus.VERIFIED:
      return 3;
    case CompanyDraftStatus.CREATING:
      return 4;
    default:
      return 1;
  }
}

export function isDraftEditable(
  draft: GetSpvDraftQuery['spvDraft'],
  user: UserInfosFragment | null,
): boolean {
  if (draft?.owner.id !== user?.id) {
    return false;
  }
  if (draft?.status) {
    return draft.status === CompanyDraftStatus.EDITING || draft.status === CompanyDraftStatus.PAYED;
  }
  return false;
}

/**
 * getIdentityBadge.
 * Return IBadge for the identity accordion section.
 *
 * @param {GetSpvDraftQuery['spvDraft']} draft
 * @param {GetBankIdentitiesByCompanyQuery['getBankIdentitiesByCompany']} bankIdentities
 */
export function getIdentityBadge(
  draft: GetSpvDraftQuery['spvDraft'],
  bankIdentities: GetBankIdentitiesByCompanyQuery['getBankIdentitiesByCompany'],
): IBadge {
  const noBankIdentities = !bankIdentities.length;
  const verificationPending = bankIdentities.some(
    (el) => el?.verificationStatus === IdentityVerificationStatus.PENDING,
  );

  if (noBankIdentities) {
    return {
      color: 'gray',
      label: 'InProgress',
    };
  }
  if (
    draft?.status &&
    [CompanyDraftStatus.VERIFIED, CompanyDraftStatus.CREATING].includes(draft?.status)
  ) {
    return {
      color: 'green',
      label: 'Verified',
    };
  }
  if (verificationPending) {
    return {
      color: 'yellow',
      label: 'VerificationInProgress',
    };
  }
  return {
    color: 'gray',
    label: 'NonVerified',
  };
}

/**
 * getDepositBadge.
 * Return IBadge for the capital deposit accordion section.
 *
 * @param {GetSpvDraftQuery['spvDraft']} draft
 * @param {BankCapitalAccount | null | undefined} bankCapitalAccount
 * @returns {IBadge}
 */
export function getDepositBadge(
  draft: GetSpvDraftQuery['spvDraft'],
  bankCapitalAccount: GetBankIdentitiesByCompanyQuery['getBankIdentitiesByCompany'][0]['bankCapitalAccount'],
): IBadge {
  if (bankCapitalAccount?.transferStatus === CreditTransferStatus.RECEIVED) {
    if (bankCapitalAccount?.balance === draft?.content?.initialShareholder?.capitalDeposit) {
      return {
        color: 'green',
        label: 'Received',
      };
    }
    return {
      color: 'yellow',
      label: 'WrongAmount',
    };
  }
  return {
    color: 'gray',
    label: 'InProgress',
  };
}

export const SPV_DOCUMENT_TYPE_TO_SIGN = [
  CompanyDocumentType.STATUTS,
  CompanyDocumentType.DOMICILIATION,
  CompanyDocumentType.SOUSCRIPTEURS,
  CompanyDocumentType.DBE,
  CompanyDocumentType.POUVOIR_FORMALITES,
  CompanyDocumentType.FORMULAIRE_M0,
  CompanyDocumentType.DNC,
];

/**
 * cleanDraftDocuments.
 * Filter out irrelevant documents from draft query.
 *
 * @param documents
 * @returns
 */
export function cleanDraftDocuments(
  documents: SpvDraftDocumentInfosFragment[],
): Record<string, SpvDraftDocumentInfosFragment> {
  return documents
    .filter((doc) => SPV_DOCUMENT_TYPE_TO_SIGN.includes(doc.type))
    .reduce((acc, cur) => ({ ...acc, [cur.type]: cur }), {});
}
